import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { WifiOff, Download, Camera, RefreshCw, HardDrive, AlertTriangle, CheckCircle2, Ban } from 'lucide-react';
import { P } from '../../lib/sitewalkApi';
import * as store from '../../lib/offlineStore';

const STATE_STYLE = {
  SAVED_ON_DEVICE: 'bg-slate-200 text-slate-700', UPLOADING: 'bg-sky-100 text-sky-700',
  SYNCED: 'bg-emerald-100 text-emerald-700', CONFLICT: 'bg-amber-100 text-amber-700',
  ACCESS_REVOKED: 'bg-red-100 text-red-700', FAILED: 'bg-red-100 text-red-700',
};
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/** Browser-Simulated Offline Site Walk (additive). In the native app this same store runs inside the
 *  device WebView's IndexedDB (build-validated, device NOT certified — no separate native filesystem
 *  path). Local records are clearly separated from authoritative server evidence. */
export default function OfflineWalkPanel({ projectId }) {
  const [walks, setWalks] = useState([]);
  const [walkId, setWalkId] = useState('');
  const [prepared, setPrepared] = useState(null);
  const [pending, setPending] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${P(projectId)}/site-walks`, { params: { pageSize: 200 } }).then((r) => setWalks(r.data.items || [])); }, [projectId]);
  const refresh = async (wid) => {
    if (!wid) return;
    setPrepared(await store.preparedWalk(wid));
    setPending(await store.listCaptures(wid));
    setEstimate(await store.storageEstimate());
  };
  useEffect(() => { refresh(walkId); /* eslint-disable-next-line */ }, [walkId]);

  const prepare = async () => {
    setBusy(true);
    try {
      const walk = (await axios.get(`${P(projectId)}/site-walks/${walkId}`)).data;
      const cps = (await axios.get(`${P(projectId)}/site-walks/${walkId}/capture-points`)).data.items || [];
      await store.prepareOffline({ walkId, walk, captureLocations: cps.map((c) => ({ id: c.captureLocationId, name: c.name })), revision: walk.drawingRevision || null });
      toast.success('Available Offline — working set stored on device');
      await refresh(walkId);
    } catch (e) { toast.error('Offline preparation failed'); }
    setBusy(false);
  };

  const captureOffline = async () => {
    try {
      // representative binary (a small blob stands in for a panorama in browser sim)
      const blob = new Blob([new Uint8Array(1024 * 200)], { type: 'image/jpeg' });
      const ck = uid();
      await store.saveCapture({ clientKey: ck, walkId, entity: 'captureLocation', tempId: `L-${ck}`,
        data: { name: `Field capture ${pending.length + 1}` }, blob, filename: 'offline.jpg', mime: 'image/jpeg', size: blob.size });
      toast.success('Saved on Device');
      await refresh(walkId);
    } catch (e) { toast.error(String(e.message || e).startsWith('STORAGE_FAILED') ? 'Storage failure — NOT saved on device' : 'Capture failed'); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const caps = await store.listCaptures(walkId);
      const items = caps.filter((c) => c.state === 'SAVED_ON_DEVICE' || c.state === 'FAILED')
        .map((c) => ({ clientKey: c.clientKey, tempId: c.tempId, entity: c.entity, parentTempId: c.parentTempId, data: c.data }));
      if (!items.length) { toast.info('Nothing pending to sync'); setBusy(false); return; }
      const r = (await axios.post(`${P(projectId)}/site-walks/${walkId}/sync`, { items })).data;
      if (r.walkAccess === 'REVOKED') {
        for (const c of caps) await store.setState(c.clientKey, { state: 'ACCESS_REVOKED' });
        toast.error('Access Changed — evidence preserved locally, resolution required');
        await refresh(walkId); setBusy(false); return;
      }
      const byKey = Object.fromEntries(r.results.map((x) => [x.clientKey, x]));
      for (const c of caps) {
        const res = byKey[c.clientKey];
        if (!res) continue;
        if (res.status === 'ACCEPTED' || res.status === 'ALREADY_SYNCED') {
          await store.setState(c.clientKey, { state: 'SYNCED', serverId: res.serverId });
          await store.cleanupSynced(c.clientKey);           // safe cleanup only after confirmed persistence
        } else if (res.status === 'CONFLICT') await store.setState(c.clientKey, { state: 'CONFLICT', error: res.detail });
        else if (res.status === 'ACCESS_REVOKED') await store.setState(c.clientKey, { state: 'ACCESS_REVOKED' });
        else await store.setState(c.clientKey, { state: 'FAILED', error: res.detail });
      }
      toast.success('Synchronized');
      await refresh(walkId);
    } catch (e) { toast.error('Sync failed — will retry (evidence preserved)'); }
    setBusy(false);
  };

  const mb = (b) => (b == null ? '—' : `${(b / 1048576).toFixed(1)} MB`);

  return (
    <div data-testid="offline-walk-panel">
      <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
        <WifiOff className="w-4 h-4 text-sky-600" /> Browser-Simulated Offline. Local records are NOT authoritative until synced. Native app reuses this WebView/IndexedDB offline store — build-validated, device NOT certified.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={walkId} onChange={(e) => setWalkId(e.target.value)} className="border rounded-lg px-2 py-1.5 text-xs" data-testid="offline-walk-select">
          <option value="">Select site walk…</option>
          {walks.map((w) => <option key={w.id} value={w.id}>{w.walkNumber}</option>)}
        </select>
        {walkId && <>
          <button onClick={prepare} disabled={busy} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg border text-slate-600" data-testid="offline-prepare-btn"><Download className="w-3.5 h-3.5" /> Prepare for Offline</button>
          {prepared && <span className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="offline-available"><CheckCircle2 className="w-3.5 h-3.5" /> Available Offline</span>}
          <button onClick={captureOffline} disabled={!prepared} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 text-white disabled:opacity-40" data-testid="offline-capture-btn"><Camera className="w-3.5 h-3.5" /> Capture (offline)</button>
          <button onClick={sync} disabled={busy} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-sky-600 text-white" data-testid="offline-sync-btn"><RefreshCw className="w-3.5 h-3.5" /> Retry Sync</button>
        </>}
      </div>

      {estimate && <p className="text-[11px] text-slate-400 mb-2 flex items-center gap-1" data-testid="offline-storage"><HardDrive className="w-3.5 h-3.5" /> Local storage: {mb(estimate.usage)} used / {mb(estimate.quota)} available</p>}

      <div className="bg-white rounded-xl border border-slate-200" data-testid="offline-pending-list">
        <div className="px-3 py-2 border-b text-sm font-semibold text-slate-700">Pending on device ({pending.filter((p) => p.state !== 'SYNCED').length})</div>
        {pending.length === 0 ? <p className="text-xs text-slate-400 p-3">No local captures. Prepare a walk and capture offline.</p> : pending.map((c) => (
          <div key={c.clientKey} className="flex items-center justify-between px-3 py-2 border-b last:border-0" data-testid={`offline-item-${c.clientKey}`}>
            <div className="min-w-0">
              <div className="text-xs font-medium text-slate-700 truncate">{c.data?.name || c.entity} · Local · Waiting for Sync</div>
              <div className="text-[10px] text-slate-400">{mb(c.size)}{c.error ? ` · ${c.error}` : ''}</div>
            </div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${STATE_STYLE[c.state] || 'bg-slate-100'}`}>
              {c.state === 'ACCESS_REVOKED' ? <span className="flex items-center gap-1"><Ban className="w-3 h-3" /> Access Changed</span> : c.state.replace(/_/g, ' ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
